import { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-apollo';

import { AlbumSlideshowForPendingLayouts } from '../../../../../../graphql/queries/albums';

const useAlbumSlideshowForPendingLayouts = ({
  albumId,
  pollingIntervalMs,
  onPollingFinished,
  maxPollingTimeMs,
}) => {
  const maxPollingTimer = useRef(null);
  const [isPollingPendingLayouts, setIsPollingPendingLayouts] = useState(false);
  const [totalLayouts, setTotalLayouts] = useState(undefined);
  const {
    data: { album: pendingLayoutsSlideshowAlbum } = {},
    startPolling: startPollingPendingLayouts,
    stopPolling: stopPollingPendingLayouts,
    refetch,
  } = useQuery(AlbumSlideshowForPendingLayouts, {
    variables: { id: albumId },
    fetchPolicy: 'network-only',
    skip: !albumId,
  });

  const { has_pending_layouts_create: hasPendingLayouts, layouts } =
    pendingLayoutsSlideshowAlbum?.slideshow ?? {};

  const onStopPollingPendingLayouts = ({
    onPollingFinished: onPollingFinishedProp,
  }) => {
    stopPollingPendingLayouts();
    setIsPollingPendingLayouts(false);
    onPollingFinishedProp?.();
  };

  const onStartPollingPendingLayouts = ({
    pollingIntervalMs: pollingIntervalMsProp,
    maxPollingTimeMs: maxPollingTimeMsProp,
  }) => {
    if (maxPollingTimeMsProp) {
      // Reset timer
      if (maxPollingTimer.current) {
        clearTimeout(maxPollingTimer.current);
        maxPollingTimer.current = null;
      }
      maxPollingTimer.current = setTimeout(
        () => onStopPollingPendingLayouts({ onPollingFinished }),
        maxPollingTimeMsProp,
      );
    }

    startPollingPendingLayouts(pollingIntervalMsProp);
    setIsPollingPendingLayouts(true);
  };

  useEffect(() => {
    if (typeof layouts?.total !== 'undefined') {
      setTotalLayouts(layouts?.total);
    }
  }, [layouts]);

  useEffect(() => {
    if (maxPollingTimeMs) {
      // Reset timer
      if (maxPollingTimer.current) {
        clearTimeout(maxPollingTimer.current);
        maxPollingTimer.current = null;
      }

      maxPollingTimer.current = setTimeout(
        () => onStopPollingPendingLayouts({ onPollingFinished }),
        maxPollingTimeMs,
      );
    }
  }, [maxPollingTimeMs]);

  useEffect(() => {
    if (pollingIntervalMs) {
      onStartPollingPendingLayouts({ pollingIntervalMs });
    }
  }, [pollingIntervalMs]);

  // This should run on every render, as we want to keep good track in order to avoid
  // the polling query running unintentioanlly.
  if (
    isPollingPendingLayouts &&
    pendingLayoutsSlideshowAlbum &&
    !hasPendingLayouts &&
    // If the timer is enabled, the timer will stop polling on it's own.
    !maxPollingTimer.current
  ) {
    onStopPollingPendingLayouts({ onPollingFinished });
  }

  return {
    totalLayouts,
    hasPendingLayouts,
    isPollingPendingLayouts,
    refetchPendingLayouts: refetch,
    onStartPollingPendingLayouts,
    onStopPollingPendingLayouts,
  };
};

export default useAlbumSlideshowForPendingLayouts;
